/** Parent-facing Year 7 path for Gravesend Grammar School. */

export const GRAVESEND_JOURNEY = {
  schoolId: 6,
  schoolName: "Gravesend Grammar School",
  kicker: "School guide",
  title: "What to do for Gravesend Grammar",
  lead:
    "A short parent guide: what the school is, how the Kent priority areas stack, how to apply through the Kent Test, results, how places are ordered, and how this school sits next to nearby grammars.",
  heroStats: [
    { label: "GCSE rank", value: "#136" },
    { label: "Gender", value: "Boys" },
    { label: "Catchment", value: "Priority + Open" },
    { label: "Oxbridge offers", value: "—" },
    { label: "Area", value: "Kent" },
  ],
  catchmentVisual: {
    kind: "map",
    aria: "Gravesend Grammar catchment map",
    caption: "",
    labels: {
      school: "Gravesend Grammar",
      layers: {
        1: {
          title: "Priority area 1 · Gravesham",
          detail: "Main borough priority around the school",
        },
        2: {
          title: "Priority area 2 · Inner parishes / Ebbsfleet",
          detail: "Named nearby parishes and Ebbsfleet",
        },
        3: {
          title: "Priority area 3 · Outer parishes",
          detail: "Further named parishes on the map",
        },
      },
      outside:
        "Outside the named areas is the open residual — still possible after selective status, but usually behind local priority.",
    },
  },
  sources: [
    {
      label: "Year 7 admissions",
      href: "https://www.gravesendgrammar.com/admissions/year-7-admissions",
    },
    {
      label: "Admission arrangements",
      href: "https://www.gravesendgrammar.com/admissions/admission-arrangements",
    },
    {
      label: "Pupil destinations",
      href: "https://www.gravesendgrammar.com/about/pupil-destinations",
    },
  ],
  steps: [
    {
      id: "overview",
      title: "1. Overview",
      summary: "What Gravesend Grammar is, in one minute",
      paragraphs: [
        "Gravesend Grammar School is a selective state grammar school for boys in Gravesend, Kent. It is not fee-paying. Boys usually take the Kent Test and then apply through their home local authority.",
        "There are 210 Year 7 places in the latest arrangements we hold. Boys need to be assessed as selective, then places are offered using the school's geography priority rules and distance.",
        "This is a useful example of a “Both” catchment: there is a clear local priority map, and also an open residual for children further out.",
      ],
    },
    {
      id: "catchment",
      title: "2. Catchment check",
      summary: "Priority layers first, then open",
      paragraphs: [
        "Start with Gravesham borough — that is the main Priority Area. Boys living there sit ahead of boys living only in the wider residual group, once everyone has passed the selective standard.",
        "Around that sit named priority parishes and the Ebbsfleet ward. After those bands comes an open residual. That does not mean catchment does not matter. It means boys from outside the named areas can still be considered after the priority geography.",
        "Once a boy is selective, Gravesend Grammar’s place order follows geography bands, then usually pupil premium and distance inside a band. So the decisive question is often which catchment layer you are in, not a single entrance-score league table.",
        "When does he need to be in catchment? Gravesend Grammar uses home address for the priority geography after the Kent Test. Use the permanent home on the CAF / SCAF by the late-October or early-November deadline for your entry year. That is the address the priority bands are checked against. If you move later, check the current arrangements — local priority can change with the home address.",
      ],
      example: {
        title: "Example",
        kind: "priority_layers",
        body:
          "Boy A lives in Gravesham, Boy B in a named priority parish, and Boy C outside the named areas. After selective status, A is usually considered before B, and B before C in the open residual. Inside one band, pupil premium and distance often decide the order.",
      },
      showCatchmentVisual: true,
      points: [
        "Priority area 1 = Gravesham borough",
        "Then named parishes / Ebbsfleet, then outer parishes",
        "Outside named areas = open residual, usually lower priority",
        "Home address for bands: permanent home on the CAF / SCAF deadline",
      ],
    },
    {
      id: "apply",
      title: "3. How to apply",
      summary: "Kent Test registration, then the CAF / SCAF",
      paragraphs: [
        "Dates below are for Year 7 entry in September 2027 (from Kent County Council and Gravesend Grammar published dates). Always re-check those pages if you are applying for a later year.",
        "For most families the route is: register for the Kent Test while your son is in Year 5, sit the test in September of Year 6, get the result in October, then name Gravesend Grammar on your home local authority common application form.",
        "For 2027 entry, Kent Test registration opened on 1 June 2026 and closed on 1 July 2026. Kent primary pupils sat on 10 September 2026; pupils outside Kent sat on 12–13 September 2026. Results were sent after 4pm on 15 October 2026. The national CAF / SCAF closing date is 2 November 2026.",
        "Passing the Kent Test makes him eligible to be considered. It does not by itself guarantee a Gravesend Grammar place if more eligible boys want the school than there are places in his geography band.",
      ],
      timeline: [
        { when: "1 June 2026", what: "Kent Test registration opens (2027 entry)" },
        { when: "1 July 2026", what: "Kent Test registration closes" },
        { when: "10 Sept 2026", what: "Kent Test — Kent primary pupils" },
        { when: "12–13 Sept 2026", what: "Kent Test — pupils outside Kent" },
        { when: "15 Oct 2026", what: "Kent Test results (after 4pm)" },
        { when: "2 Nov 2026", what: "CAF / SCAF national closing date" },
        { when: "1 March 2027", what: "National Offer Day" },
      ],
    },
    {
      id: "exams",
      title: "4. Exams and preparation",
      summary: "The Kent Test",
      paragraphs: [
        "Gravesend Grammar uses the Kent Test rather than its own private first paper. The familiar layout is a reasoning paper plus English and maths.",
        "A selective Kent Test result is the usual gateway to Kent’s grammar schools as a group — there are about thirty-plus Kent grammars that use it for selective assessment (boys’, girls’, and mixed). You do not sit a separate paper for each of them. You still name only the schools you want on the CAF / SCAF, and each school then applies its own catchment and oversubscription rules. Medway grammars often use a different test, so do not assume a Kent result covers Medway.",
        "Preparation is steady reading, accurate maths, and calm timed practice. Use Kent County Council's published familiarisation materials and the school's admissions pages for the current year.",
        "If your son is not automatically selective on the test score alone, Kent has a headteacher assessment route for some children. That is a county process, not a Gravesend-only paper.",
      ],
      points: [
        "Kent Test — reasoning, English, maths",
        "One selective result for Kent grammars — still name each school on the CAF",
        "Check Kent familiarisation materials each year",
      ],
    },
    {
      id: "place-order",
      title: "5. How places are ordered",
      summary: "The offer list, step by step",
      paragraphs: [
        "Once boys are selective, Gravesend Grammar follows a geography-led place order. Think of it as a stack of trays: local trays first, then wider trays.",
        "After EHCP and the early priority groups, Gravesham residents come before the named parish layers, and those come before the open residual. Inside each tray, pupil premium usually comes before other eligible boys, and closer homes usually come before further ones.",
        "So the practical questions are: is he selective, which tray are we in, and how close are we within that tray?",
      ],
      placeOrder: [
        { title: "EHCP / looked-after", detail: "Handled at the front under the published rules" },
        { title: "Other early priority groups", detail: "For example medical / social, siblings, or staff, as the arrangements set out" },
        { title: "Gravesham residents", detail: "Main Priority Area around the school" },
        { title: "Inner priority parishes + Ebbsfleet", detail: "Named nearby areas on the map" },
        { title: "Outer priority parishes", detail: "A shorter further parish list" },
        { title: "Open residual", detail: "Eligible boys outside the named areas" },
        { title: "Inside each band", detail: "Usually pupil premium first, then closer distance" },
      ],
    },
    {
      id: "results",
      title: "6. Results",
      summary: "Where Gravesend Grammar sits among grammars",
      paragraphs: [
        "Among grammar schools, Gravesend Grammar’s latest GCSE Attainment 8 sits a little below the grammar average, and lower than the very highest schools. That still places it firmly inside the grammar range — a solid mid-to-lower set of results, not a weak one.",
        "At A level the average grade is a B, again a little under the grammar mean. Progress was published as above average in the latest year we hold, which is a useful balance to the headline points score.",
        "We do not currently show a separate Oxbridge offer count for Gravesend in SchoolCatch. The school publishes destinations on its own site if you want that picture too.",
      ],
      facts: [
        { label: "GCSE rank", value: "#136" },
        { label: "GCSE Attainment 8", value: "65.3" },
        { label: "A level average", value: "B" },
        { label: "A level A*–A", value: "33%" },
      ],
    },
    {
      id: "allocation",
      title: "7. Places and demand",
      summary: "Kent Test first, then offer day",
      paragraphs: [
        "The first exam here is the county Kent Test, not a Gravesend-only paper. Boys register in Year 5, sit in September of Year 6, and get the result before CAF choices are final. Only boys assessed as selective move forward for grammar places.",
        "That first filter is wider than one school: it decides grammar eligibility across Kent. Gravesend Grammar then applies its own geography and distance rules among eligible boys.",
        "On National Offer Day, first preferences are close to the number of offers, but that does not make the school automatic. Living outside Gravesham — in the parish layers or the open residual — is where many families overestimate their chances.",
      ],
      facts: [
        { label: "First exam", value: "Kent Test" },
        { label: "First preferences", value: "254" },
        { label: "Offers", value: "209" },
        { label: "All preferences", value: "642" },
      ],
    },
    {
      id: "nearby",
      title: "8. Nearby schools",
      summary: "How Gravesend Grammar sits beside local grammars",
      paragraphs: [
        "The closest grammar neighbour is Mayfield Grammar School, Gravesend — the girls' school in the same town. Further out, families often also look at Rochester and Medway grammars such as Rochester Grammar, Sir Joseph Williamson's, and Fort Pitt.",
        "On GCSE Attainment 8, Gravesend Grammar sits close to Mayfield and Fort Pitt, and a little below Rochester Grammar and Sir Joseph Williamson's in the latest set we hold. Use that as local context while you compare catchments and travel.",
      ],
      nearby: [
        { name: "Mayfield Grammar School, Gravesend", detail: "About 2 miles · girls' grammar in the same town · similar GCSE Attainment 8 band" },
        { name: "Fort Pitt Grammar School", detail: "About 11 miles · Medway · similar GCSE band" },
        { name: "The Rochester Grammar School", detail: "About 11 miles · a little higher GCSE Attainment 8 in the latest set" },
        { name: "Sir Joseph Williamson's Mathematical School", detail: "About 11 miles · boys' grammar · a little higher GCSE Attainment 8" },
      ],
    },
    {
      id: "admission",
      title: "9. Offer day",
      summary: "From the list to March",
      paragraphs: [
        "National Offer Day is at the start of March. By then selective status and the geography place order have already been applied.",
        "Re-check the school's admission arrangements for the exact year you are entering — parish lists and small rules can move.",
      ],
    },
  ],
};
